import { ReconciliationSupplierRow, useGetReconciliationSupplierDetailsApi } from 'apis/reconciliation';
import dayjs from 'dayjs';
import { Box, Button, Flex, HStack, Icon, Link, Skeleton, Stack, Td, Text, Tooltip, Tr } from 'design-system';
import { CollectionPanel } from 'pages/reconciliation/components/collection-panel';
import { GmvByRestaurantTable } from 'pages/reconciliation/components/gmv-by-restaurant-table';
import { InvoicesList } from 'pages/reconciliation/components/invoices-list';
import { SupplierOrdersModal } from 'pages/reconciliation/components/supplier-orders-modal';
import {
  formatAed,
  formatVariance,
  getRevenueTooltip,
  getStatusLabel,
  STATUS_META,
  varianceColor,
} from 'pages/reconciliation/utils/format';
import { Fragment, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

export interface ISupplierRow {
  row: ReconciliationSupplierRow;
  month: string;
  isExpanded: boolean;
  onToggle: () => void;
  /** Opens the shared Add SOA modal pre-selected for this supplier (see reconciliation-screen.tsx). */
  onEditSoa: () => void;
}

export const SupplierRow = ({ row, month, isExpanded, onToggle, onEditSoa }: ISupplierRow) => {
  const restaurantsLabel = `${row.restaurantsCount} ${row.restaurantsCount === 1 ? 'restaurant' : 'restaurants'}`;

  const bd = row.commissionBreakdown;
  const totalOrders = bd ? bd.percentOrders + bd.fixedOrders + (bd.waivedOrders ?? 0) + bd.pendingTerms : null;
  const ordersLabel = totalOrders !== null ? `${totalOrders} ${totalOrders === 1 ? 'order' : 'orders'}` : null;
  const monthStart = `${month}-01`;
  const monthEnd = dayjs(monthStart).endOf('month').format('YYYY-MM-DD');

  // `?? open` mirrors getStatusLabel's fallback — an unknown BE status value (deploy gap /
  // future additions) degrades to a stale "Open" look instead of a TypeError blanking the table.
  const statusMeta = STATUS_META[row.status] ?? STATUS_META.open;
  const statusLabel = getStatusLabel(row);

  // State-as-disclosure (same variant as quote-workbench's EditItemModal): the payload of which
  // scope to open doubles as the isOpen flag. Restaurant rows open scoped; the collection panel's
  // "View unpaid orders" opens supplier-wide with the unpaid filter preset.
  const [ordersModal, setOrdersModal] = useState<{
    restaurantId: number | null;
    restaurantName: string | null;
    unpaidOnly: boolean;
  } | null>(null);

  // Only fetched once the row is expanded — the hook is a no-op (enabled: false) when supplierId is null.
  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useGetReconciliationSupplierDetailsApi(month, isExpanded ? row.supplierId : null);

  return (
    <Fragment>
      <Tr cursor='pointer' onClick={onToggle} _hover={{ bg: '#FAFBFC' }}>
        <Td w='44px' textAlign='center'>
          <Icon
            as={FiChevronDown}
            color='gray.400'
            boxSize='14px'
            transform={isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
            transition='transform 0.15s'
          />
        </Td>

        {/* No width — matches the widthless Supplier Th in reconciliation-screen.tsx;
            under the table's fixed layout this column absorbs all leftover width. */}
        <Td>
          <HStack spacing={1} align='baseline'>
            <Text fontWeight='semibold' color='gray.700' isTruncated>
              {row.supplierName}
            </Text>
            <Text fontSize='xs' color='gray.400' whiteSpace='nowrap'>
              · {restaurantsLabel}
              {ordersLabel && ` · ${ordersLabel}`}
              {bd && bd.fixedOrders > 0 && ` · ${bd.fixedOrders} fixed`}
            </Text>
            {bd && bd.pendingTerms > 0 && (
              <Link
                as={RouterLink}
                to={`/app/gpo-orders/list?supplierId=${row.supplierId}&dateFrom=${monthStart}&dateTo=${monthEnd}`}
                onClick={(e) => e.stopPropagation()}
                fontSize='xs'
                color='orange.500'
                fontWeight='medium'
                whiteSpace='nowrap'>
                · {bd.pendingTerms} missing commission
              </Link>
            )}
          </HStack>
        </Td>

        <Td isNumeric w='128px'>
          {formatAed(row.platformGmv)}
        </Td>

        <Td isNumeric w='128px' color={row.soaGmv === null ? 'gray.400' : undefined}>
          {row.soaGmv === null ? 'Not entered' : formatAed(row.soaGmv)}
        </Td>

        <Td isNumeric w='106px' color={varianceColor(row.variance)} fontWeight='semibold'>
          {formatVariance(row.variance)}
        </Td>

        <Td isNumeric w='118px'>
          {/* The Text wrapper is what makes the tooltip work in a table cell — Tooltip needs a
              single focusable/hoverable child, not the Td's text node. */}
          <Tooltip label={getRevenueTooltip(row)} placement='top' hasArrow>
            <Text as='span'>{formatAed(row.revenue)}</Text>
          </Tooltip>
        </Td>

        <Td w='200px'>
          <HStack spacing='xs'>
            <Box w='8px' h='8px' borderRadius='full' bg={statusMeta.dotColor} flexShrink={0} />
            <Text fontSize='sm' fontWeight='medium' color={statusMeta.textColor} whiteSpace='nowrap'>
              {statusLabel}
            </Text>
          </HStack>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr>
          <Td colSpan={7} p='0'>
            {/*
              whiteSpace='normal' below undoes Chakra's TableContainer, which sets
              `white-space: nowrap` on the ancestor `.chakra-table__container` div (see
              @chakra-ui/table's table-container.tsx). That nowrap inherits into every
              descendant text node in this subtree — combined with flex/grid children's
              default `min-width: auto`, the min-content width of any un-truncated text
              becomes its full unwrapped line width, forcing the whole expanded block
              (and the nested GmvByRestaurantTable) wider than the card no matter how
              much viewport space is actually available. Resetting it here is the fix
              for the "overflows even at 3000px" symptom; truncated chips/links still
              opt back into nowrap explicitly via `isTruncated`.
            */}
            <Box
              bg='#FAFBFC'
              borderTop='1px solid'
              borderColor='gray.100'
              pt='18px'
              pr='24px'
              pb='24px'
              pl='44px'
              whiteSpace='normal'>
              <Flex gap='36px' align='flex-start' wrap='wrap'>
                {/* flex-basis 480px (not `flex='1'` = basis 0) is load-bearing: against the right
                    panel's `1 1 380px`, a zero basis makes the flex algorithm hand the right panel
                    all the freed space and pin this section at its 360px min — which is narrower
                    than the 5-column breakdown table's ~445px min-content, forcing a spurious
                    internal scrollbar even on wide screens. A real 480px basis keeps this section
                    at ≥~480px whenever both panels are side by side, so the table fits scroll-free;
                    the 360px min still lets it shrink before the panel wraps below. */}
                {isDetailLoading ? (
                  <Stack spacing='sm' flex='1 1 480px' minW='360px'>
                    <Skeleton height='20px' />
                    <Skeleton height='20px' />
                    <Skeleton height='20px' />
                  </Stack>
                ) : isDetailError ? (
                  <Stack justify='center' flex='1 1 480px' minW='360px' minH='80px'>
                    <Text color='gray.500' fontSize='sm' textAlign='center'>
                      Could not load details
                    </Text>
                  </Stack>
                ) : (
                  <Stack spacing='md' flex='1 1 480px' minW='360px'>
                    <GmvByRestaurantTable
                      restaurants={detail?.restaurants ?? []}
                      totals={{
                        platformGmv: row.platformGmv,
                        soaGmv: row.soaGmv,
                        variance: row.variance,
                        commission: row.revenue,
                      }}
                      showCommission={!!row.commissionBreakdown}
                      onViewOrders={({ restaurantId, restaurantName }) =>
                        setOrdersModal({ restaurantId, restaurantName, unpaidOnly: false })
                      }
                      headerAction={
                        // Hidden while closed — the SOA save endpoint would 409 on a closed month;
                        // reopen from the collection panel first.
                        row.status !== 'closed' ? (
                          <Button size='xs' variant='outline' onClick={onEditSoa}>
                            Edit SOA
                          </Button>
                        ) : undefined
                      }
                    />

                    {!!detail?.documents.length && (
                      <Stack spacing='xs' pt='sm' borderTop='1px solid' borderColor='gray.100'>
                        <Text
                          fontSize='2xs'
                          fontWeight='bold'
                          color='gray.500'
                          textTransform='uppercase'
                          letterSpacing='wide'>
                          Statements
                        </Text>
                        {detail.documents.map((doc) => (
                          <HStack key={doc.id} justify='space-between' spacing='sm'>
                            <Link
                              href={doc.url}
                              target='_blank'
                              rel='noopener noreferrer'
                              color='red.500'
                              fontSize='sm'
                              isTruncated>
                              {doc.fileName}
                            </Link>
                            <Text fontSize='xs' color='gray.500' whiteSpace='nowrap'>
                              {doc.restaurantName ?? 'Supplier-wide'}
                            </Text>
                          </HStack>
                        ))}
                      </Stack>
                    )}

                    {!!detail?.invoices?.length && <InvoicesList month={month} detail={detail} />}
                  </Stack>
                )}

                {/* Flexible basis instead of a fixed 420px — lets the panel shrink on
                    narrower cards (down to minW) and wrap fully below the breakdown
                    table via the parent Flex's wrap='wrap' once space runs out,
                    rather than forcing a fixed-width overflow. */}
                <Box flex='1 1 380px' minW='340px' maxW='560px' borderLeft='1px solid' borderColor='gray.100' pl='36px'>
                  {isDetailLoading ? (
                    <Stack spacing='sm'>
                      <Skeleton height='20px' />
                      <Skeleton height='20px' />
                      <Skeleton height='20px' />
                    </Stack>
                  ) : isDetailError || !detail ? (
                    <Stack justify='center' minH='80px'>
                      <Text color='gray.500' fontSize='sm' textAlign='center'>
                        Could not load details
                      </Text>
                    </Stack>
                  ) : (
                    <CollectionPanel
                      month={month}
                      detail={detail}
                      onViewUnpaidOrders={() =>
                        setOrdersModal({ restaurantId: null, restaurantName: null, unpaidOnly: true })
                      }
                    />
                  )}
                </Box>
              </Flex>

              <SupplierOrdersModal
                isOpen={!!ordersModal}
                onClose={() => setOrdersModal(null)}
                month={month}
                supplierId={row.supplierId}
                supplierName={row.supplierName}
                restaurantId={ordersModal?.restaurantId ?? null}
                restaurantName={ordersModal?.restaurantName ?? null}
                defaultUnpaidOnly={ordersModal?.unpaidOnly ?? false}
              />
            </Box>
          </Td>
        </Tr>
      )}
    </Fragment>
  );
};
